import type { Labelled } from './common.js';

/**
 * عمولات المبيعات.
 *
 * العمولة هنا نتيجة محسوبة من المستندات لا رقم يُكتب بيد: يُنسب لكل مندوب ما
 * يملكه من فواتير، ويُحتسب على أساس معلن ونسبة معلنة. كتابة المبلغ يدويًا
 * تجعل الخلاف مع المندوب خلافًا على رقم لا أصل له.
 *
 * وتُصرف مع المرتب — بندًا في القسيمة — لا في مسار دفع ثانٍ: مساران يدفعان
 * للناس يعنيان مبلغًا يُصرف مرتين أو لا يُصرف أصلًا.
 */

/* ==========================================================================
   أساس الاحتساب
   ========================================================================== */

export const COMMISSION_BASES = ['COLLECTED', 'INVOICED'] as const;
export type CommissionBasis = (typeof COMMISSION_BASES)[number];

export interface CommissionBasisDef extends Labelled<CommissionBasis> {
  description: string;
  /** ما يظهر في رأس العمود أمام رقم الأساس. */
  amountLabel: string;
}

export const COMMISSION_BASIS_DEFS: readonly CommissionBasisDef[] = [
  {
    key: 'COLLECTED',
    label: 'المحصَّل',
    kind: 'success',
    amountLabel: 'المحصَّل خلال الشهر',
    description:
      'ما دخل الخزينة فعلًا من فواتير المندوب. يربط العمولة بالنقد، فلا تُصرف على بيع لم يُسدَّد.',
  },
  {
    key: 'INVOICED',
    label: 'المفوتَر',
    kind: 'info',
    amountLabel: 'المفوتَر خلال الشهر',
    description:
      'ما أصدره المندوب من فواتير في الشهر. يكافئ البيع فور إتمامه، وتتحمل الشركة عبء التحصيل.',
  },
] as const;

export const COMMISSION_BASIS_MAP = Object.fromEntries(
  COMMISSION_BASIS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CommissionBasis, CommissionBasisDef>>;

/* ==========================================================================
   سياسة الهدف
   ========================================================================== */

export const COMMISSION_TARGET_POLICIES = ['NONE', 'ABOVE_TARGET', 'GATE'] as const;
export type CommissionTargetPolicy = (typeof COMMISSION_TARGET_POLICIES)[number];

export interface CommissionTargetPolicyDef extends Labelled<CommissionTargetPolicy> {
  description: string;
}

export const COMMISSION_TARGET_POLICY_DEFS: readonly CommissionTargetPolicyDef[] = [
  {
    key: 'NONE',
    label: 'بلا هدف',
    kind: 'neutral',
    description: 'العمولة على كل ما تحقق من أول جنيه.',
  },
  {
    key: 'ABOVE_TARGET',
    label: 'على ما زاد عن الهدف',
    kind: 'info',
    description: 'لا عمولة حتى الهدف، وما بعده خاضع. يجعل الهدف حدًّا لا مكافأة.',
  },
  {
    key: 'GATE',
    label: 'الهدف شرط',
    kind: 'warning',
    description:
      'بلوغ الهدف شرط للاستحقاق، وعنده تُحتسب العمولة على المبلغ كاملًا. حافز قوي وقاسٍ: من قصّر جنيهًا واحدًا لا يأخذ شيئًا.',
  },
] as const;

export const COMMISSION_TARGET_POLICY_MAP = Object.fromEntries(
  COMMISSION_TARGET_POLICY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CommissionTargetPolicy, CommissionTargetPolicyDef>>;

/* ==========================================================================
   مصدر الهدف
   ========================================================================== */

/**
 * من أين جاء هدفُ الشهر الذي قيس به المندوب.
 *
 * الهدف صار خطة مفترة: سنةٌ تنزل أرباعًا وشهورًا يضبطها المدير لكل مندوب.
 * وقبلها كان رقمًا واحدًا في ملف الموظف لا شهر له. والرقمان قد يختلفان،
 * والفرق بينهما مالٌ يُصرف أو يُمنع — فيُجمَّد في السطر أيُّهما قاس.
 *
 * وبلا ذلك يقرأ المدير «الهدف ٥٠٬٠٠٠» ولا يعرف أهو ما خطّطه لهذا الشهر أم
 * رقمٌ قديمٌ نسيه في ملف قديم، فيمرّ خطأ الصرف بلا أن يلحظه أحد.
 */
export const COMMISSION_TARGET_SOURCES = ['PLAN', 'LEGACY', 'NONE'] as const;
export type CommissionTargetSource = (typeof COMMISSION_TARGET_SOURCES)[number];

export interface CommissionTargetSourceDef extends Labelled<CommissionTargetSource> {
  description: string;
}

export const COMMISSION_TARGET_SOURCE_DEFS: readonly CommissionTargetSourceDef[] = [
  {
    key: 'PLAN',
    label: 'من خطة السنة',
    kind: 'success',
    description: 'هدفُ هذا الشهر بعينه من خطة المندوب في صفحة فريق المبيعات.',
  },
  {
    key: 'LEGACY',
    label: 'من ملف الموظف',
    kind: 'warning',
    description:
      'لا خطة لهذا الشهر، فقيس بالهدف الثابت في ملف الموظف. وهو رقم لا شهر له: يبقى كما كُتب وإن تغيّرت الخطة. اضبط خطة المندوب ليقاس بشهره.',
  },
  {
    key: 'NONE',
    label: 'بلا هدف',
    kind: 'neutral',
    description: 'لا خطة ولا هدف في الملف — والعمولة على كل ما تحقق.',
  },
] as const;

export const COMMISSION_TARGET_SOURCE_MAP = Object.fromEntries(
  COMMISSION_TARGET_SOURCE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CommissionTargetSource, CommissionTargetSourceDef>>;

/* ==========================================================================
   الاحتساب
   ========================================================================== */

export interface CommissionInput {
  /** ما تحقق للمندوب في الشهر على الأساس المعتمد. */
  basisAmount: number;
  /** هدف الشهر، أو null لمن لا هدف له. */
  target: number | null;
  /** نسبة العمولة المئوية. */
  rate: number;
  policy: CommissionTargetPolicy;
}

export interface CommissionResult {
  /** الجزء الخاضع للعمولة بعد تطبيق سياسة الهدف. */
  eligible: number;
  commission: number;
  /** نسبة بلوغ الهدف مئويًا، أو null بلا هدف. */
  achievement: number | null;
  /** سبب عدم الاستحقاق، إن لم يستحق شيئًا رغم وجود مبيعات. */
  blockedReason: string | null;
}

/** تقريب إلى قرشين — الأرقام هنا نقود تُصرف. */
function money(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * احتساب عمولة مندوب في شهر.
 *
 * دالة خالصة بلا قاعدة بيانات: هذه هي القاعدة التي يُحاسب عليها الناس،
 * فيجب أن تُختبر وحدها بأرقام صريحة.
 */
export function computeCommission(input: CommissionInput): CommissionResult {
  const basis = Math.max(0, input.basisAmount);
  const target = input.target != null && input.target > 0 ? input.target : null;
  const achievement = target ? money((basis / target) * 100) : null;

  let eligible = basis;
  let blockedReason: string | null = null;

  if (target) {
    if (input.policy === 'ABOVE_TARGET') {
      eligible = Math.max(0, basis - target);
      if (eligible === 0 && basis > 0) {
        blockedReason = 'لم يتجاوز الهدف، والعمولة على ما زاد عنه.';
      }
    } else if (input.policy === 'GATE' && basis < target) {
      eligible = 0;
      blockedReason = 'لم يبلغ الهدف، وبلوغه شرط للاستحقاق.';
    }
  }

  return {
    eligible: money(eligible),
    commission: money((eligible * input.rate) / 100),
    achievement,
    blockedReason,
  };
}
